import classNames from 'classnames';
import PropTypes from 'prop-types';
import { memo } from 'react';

import {
  useAuth,
  useSingleCase,
  useTownAssets,
  useTownConfig,
} from '../../../../stores';
import {
  displayLongDate,
  resolveStaffName,
  taskFlowSettings,
} from '../../../../utils';

import { MessageText } from './components';

// Step nouns live in the tasks translation section so task pages reuse them
const stepNounKeys = {
  accept: 'stepAccept',
  start: 'stepStart',
  pause: 'stepPause',
  end: 'stepEnd',
  proofPicture: 'endPhoto',
  checklist: 'stepChecklist',
  checkpointScan: 'stepCheckpointScan',
  quoteSubmit: 'stepQuoteSubmit',
  technicalAccept: 'stepTechnicalAccept',
  financialAccept: 'stepFinancialAccept',
  deliveryAccept: 'stepDeliveryAccept',
  completionAccept: 'stepCompletionAccept',
  paymentRelease: 'stepPaymentRelease',
};

// Steps the chat can translate outright; the others fall back to the generic step sentence
const decisionKeys = {
  completionAccept: {
    approved: { mine: 'eventTaskWorkApprovedByMe', other: 'eventTaskWorkApproved' },
    refused: { mine: 'eventTaskWorkRefusedByMe', other: 'eventTaskWorkRefused' },
  },
};

// Renders any chng.task event from the payload shape alone: creation shows the
// assignment sentence with its step list, a decision step (has ok) shows
// approval/refusal, a record step shows its noun, anything else falls back to
// a generic update line — a task event never dumps raw content.
const ChatTaskEvent = memo(
  ({
    task,
    author,
    participantsRef,
    groupsMap,
  }) => {
    const { assets } = useTownAssets();
    const { translate, language } = assets;
    const { thisCase } = useSingleCase();
    const { townConfig } = useTownConfig();
    const { auth } = useAuth();

    const stepNoun = (type, capitalize = true) => {
      const key = stepNounKeys[type];
      return (key) ? translate(key, 'tasks', undefined, capitalize) : type;
    };

    const resolveName = (rlid) => resolveStaffName(
      rlid,
      {
        participants: thisCase.participants,
        staffMap: townConfig.staffMap,
        authInfo: auth.info,
      },
    );

    const targetNames = (task.tgts ?? [])
      .map((tgt) => {
        // The case's own participants first: the chat's map is a ref, so a
        // target who joined with this very event is not in it yet and would
        // leave the line reading as an assignment to nobody until a reload
        if (tgt.crUid) {
          return (thisCase.participants?.find((one) => (one.crUid === tgt.crUid))?.name
            ?? participantsRef.current[tgt.crUid]?.name);
        }
        if (tgt.gpid) return groupsMap.get(tgt.gpid)?.grpLbl;
        if (tgt.piid) return translate('partner', undefined, undefined, false);
        return undefined;
      })
      .filter(Boolean);

    const settingsList = (flow) => {
      const settings = taskFlowSettings(flow, translate, resolveName);
      if (!settings.length) return null;

      return (
        <ul
          key="steps"
          className={classNames(
            'list-disc pl-5 my-1 w-fit text-left text-base',
            { 'ml-auto': !author },
          )}
        >
          {settings.map((setting, index) => (
            <li key={`${setting.type}${index}`}>
              {setting.label}
              {(setting.opt) ? ` (${translate('optional', undefined, undefined, false)})` : ''}
            </li>
          ))}
        </ul>
      );
    };

    const dateLine = (key, labelKey, iso) => (
      <MessageText
        key={key}
        author={author}
        message={`${translate(labelKey, 'tasks')} : ${
          (iso)
            ? displayLongDate(new Date(iso), language?.language)
            : translate('noDeadline', 'tasks', undefined, false)}`}
      />
    );

    const pieces = [];

    const creation = task.flow && (!task.step) && (!task.done) && (!task.cancel)
      && (task.tgts || task.reqRlid || task.desc);

    if (task.holder) {
      // Taking a team's task on answers the acceptance it owed, so the event
      // may carry that step too: one act, one line
      pieces.push(
        <MessageText
          key="holder"
          author={author}
          italic
          message={translate(
            'eventTaskHolder',
            'chat',
            { name: resolveName(task.holder.rlid) ?? task.holder.name ?? '' },
          )}
        />,
      );
    } else if (creation) {
      const base = (task.reqRlid) ? 'eventTaskAssignCase' : 'eventTaskCreate';
      const headKey = (targetNames.length) ? base : `${base}NoTgt`;
      pieces.push(
        <MessageText
          key="head"
          author={author}
          italic
          message={`${translate(headKey, 'chat', { tgts: targetNames.join(', ') })}\u00A0:`}
        />,
        settingsList(task.flow),
      );
    } else if (task.step) {
      const {
        type, ok, note, fileIds, items,
      } = task.step;

      // An author name is only carried for somebody else's message: no author = the reader did it
      const named = decisionKeys[type]?.[(ok) ? 'approved' : 'refused'];

      let headline;
      if (ok === undefined) {
        headline = stepNoun(type);
      } else if (named) {
        headline = translate((author) ? named.other : named.mine, 'chat');
      } else if (type === 'accept') {
        headline = translate((ok) ? 'eventTaskAccepted' : 'eventTaskDeclined', 'chat');
      } else {
        const stepKey = (ok) ? 'eventTaskStepApproved' : 'eventTaskStepRefused';
        headline = translate(stepKey, 'chat', { step: stepNoun(type, false) });
      }
      // The written reason is the line below, so the sentence announces it
      if (note && (ok !== undefined)) {
        headline = `${headline}, ${translate('forTheFollowingReason', 'chat', undefined, false)}\u00A0:`;
      }
      pieces.push(<MessageText key="step" author={author} italic message={headline} />);

      if (fileIds?.length) {
        pieces.push(
          <MessageText
            key="photos"
            author={author}
            message={`${fileIds.length} ${translate('pictures', 'chat', undefined, false)}`}
          />,
        );
      }
      if (items?.length) {
        const checkedCnt = items.filter((item) => item.ok).length;
        pieces.push(
          <MessageText
            key="checked"
            author={author}
            message={translate('cntOutOfTotal', undefined, { cnt: checkedCnt, total: items.length })}
          />,
        );
      }
      if (note) pieces.push(<MessageText key="note" author={author} italic message={note} />);
    } else if (task.done || task.cancel) {
      pieces.push(
        <MessageText
          key="terminal"
          author={author}
          italic
          message={translate(task.done ? 'eventTaskDone' : 'eventTaskCancel', 'chat')}
        />,
      );
    } else {
      pieces.push(
        <MessageText
          key="update"
          author={author}
          italic
          message={translate('eventTaskUpdate', 'chat')}
        />,
      );
      if (task.flow) pieces.push(settingsList(task.flow));
    }

    if (task.desc) pieces.push(<MessageText key="desc" author={author} message={task.desc} />);
    if ('startDue' in task) pieces.push(dateLine('startDue', 'plannedStart', task.startDue));
    if ('endDue' in task) pieces.push(dateLine('endDue', 'plannedEnd', task.endDue));

    return pieces;
  },
);
ChatTaskEvent.displayName = 'ChatTaskEvent';

ChatTaskEvent.propTypes = {
  task: PropTypes.shape({
    flow: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.string.isRequired,
      opt: PropTypes.number,
    })),
    tgts: PropTypes.arrayOf(PropTypes.shape({
      crUid: PropTypes.number,
      gpid: PropTypes.string,
      piid: PropTypes.string,
    })),
    reqRlid: PropTypes.string,
    holder: PropTypes.shape({
      rlid: PropTypes.string,
      name: PropTypes.string,
    }),
    desc: PropTypes.string,
    startDue: PropTypes.string,
    endDue: PropTypes.string,
    step: PropTypes.shape({
      type: PropTypes.string.isRequired,
      ok: PropTypes.number,
      note: PropTypes.string,
      fileIds: PropTypes.arrayOf(PropTypes.number),
      items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        ok: PropTypes.number,
      })),
    }),
    done: PropTypes.number,
    cancel: PropTypes.number,
  }).isRequired,
  author: PropTypes.string,
  participantsRef: PropTypes.shape({
    current: PropTypes.shape({}),
  }).isRequired,
  groupsMap: PropTypes.instanceOf(Map).isRequired,
};

export default ChatTaskEvent;
